"use strict";
/**
 * @packageDocumentation
 * @module Claimer
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildCredential = exports.requestAttestation = exports.createPresentation = void 0;
const tslib_1 = require("tslib");
const core_1 = require("@kiltprotocol/core");
const messaging_1 = tslib_1.__importDefault(require("@kiltprotocol/messaging"));
/**
 * Creates a presentation for an arbitrary amount of [[AttestedClaim]]s which can be verified in [[verifyPresentation]].
 *
 * @param identity The Claimer [[Identity]] which owns the [[AttestedClaim]]s.
 * @param message The message which represents multiple [[CType]]s, [[IRequestClaimsForCTypes]]s and whether privacy
 * enhancement is supported.
 * @param verifier The [[IPublicIdentity]] of the verifier that requested the presentation.
 * @param credentials The [[AttestedClaim]]s which should be verified.
 * @throws [[ERROR_PE_MISMATCH]], [[ERROR_MESSAGE_TYPE]], [[ERROR_PE_CREDENTIAL_MISSING]].
 * @returns A message which represents either an array of [[AttestedClaim]]s if privacy enhancement is not supported
 * or a combined presentation. Both of these options can be verified.
 */
function createPresentation(identity, message, verifier, credentials) {
    // did we get the right message type?
    if (message.body.type !== messaging_1.default.BodyType.REQUEST_CLAIMS_FOR_CTYPES) {
        throw core_1.SDKErrors.ERROR_MESSAGE_TYPE(message.body.type, messaging_1.default.BodyType.REQUEST_CLAIMS_FOR_CTYPES);
    }
    // get attributes requested by the verifier
    const requestedAttributes = credentials.map((cred) => Array.from(cred.getAttributes()));
    // create presentation for each credential
    const attestedClaims = credentials.map((cred, i) => {
        const presentation = cred.createPresentation(requestedAttributes[i]);
        // remove to show as few as possible
        return presentation;
    });
    return new messaging_1.default({
        type: messaging_1.default.BodyType.SUBMIT_CLAIMS_FOR_CTYPES,
        content: attestedClaims,
    }, identity.getPublicIdentity(), verifier);
}
exports.createPresentation = createPresentation;
/**
 * Creates a [[Message]] containing the [[IRequestAttestationForClaim]] generated from the provided [[IClaim]].
 *
 * @param claim The [[Claim]] which should get attested.
 * @param identity The Claimer's [[Identity]] which owns the [[Claim]].
 * @param attesterPublicIdentity The public identity of the attester which should attest the [[Claim]].
 * @param option The option object.
 * @param option.legitimations An Array of [[AttestedClaim]] objects of the Attester which the Claimer requests to
 * include into the [[Attestation]] as legitimations.
 * @param option.delegationId The unique identifier of the desired delegation.
 * @returns A message containing an [[IRequestAttestationForClaim]] and a [[ClaimerAttestationSession]] which together with an [[ISubmitAttestationForClaim]]
 * object can be used to create an [[AttestedClaim]].
 */
function requestAttestation(claim, identity, attesterPublicIdentity, option = {}) {
    const request = core_1.RequestForAttestation.fromClaimAndIdentity(claim, identity, option);
    const message = {
        content: {
            requestForAttestation: request,
        },
        type: messaging_1.default.BodyType.REQUEST_ATTESTATION_FOR_CLAIM,
    };
    return {
        message: new messaging_1.default(message, identity.getPublicIdentity(), attesterPublicIdentity),
        session: { requestForAttestation: request },
    };
}
exports.requestAttestation = requestAttestation;
/**
 * Builds a [[AttestedClaim]] which can be verified when used in [[createPresentation]].
 *
 * @param message The session object corresponding to the [[ISubmitAttestationForClaim]].
 * @param session The [[ClaimerAttestationSession]] which corresponds to the message and [[AttestedClaim]].
 * @returns A signed and valid [[AttestedClaim]].
 */
function buildCredential(message, session) {
    if (message.body.type !== messaging_1.default.BodyType.SUBMIT_ATTESTATION_FOR_CLAIM) {
        throw core_1.SDKErrors.ERROR_MESSAGE_TYPE(message.body.type, messaging_1.default.BodyType.SUBMIT_ATTESTATION_FOR_CLAIM);
    }
    return core_1.AttestedClaim.fromRequestAndAttestation(session.requestForAttestation, message.body.content.attestation);
}
exports.buildCredential = buildCredential;
